import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductShowcase = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'question-bank',
      title: 'Question Bank',
      description: 'Comprehensive library of contextual questions mapped to SAP Best Practices with AI-powered recommendations',
      icon: 'MessageSquare',
      color: 'bg-blue-500',
      benefits: ['10,000+ curated questions', 'Context-aware filtering', 'Custom question builder'],
      thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=250&fit=crop'
    },
    {
      id: 'context-engine',
      title: 'Context Engine',
      description: 'Intelligent system that maintains organizational context and decision history across all engagements',
      icon: 'Brain',
      color: 'bg-purple-500',
      benefits: ['Organization profiling', 'Decision tracking', 'Pattern recognition'],
      thumbnail: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop'
    },
    {
      id: 'config-directory',
      title: 'Config Directory',
      description: 'IMG-like hierarchical configuration directory with value tables and implementation guides',
      icon: 'FolderTree',
      color: 'bg-green-500',
      benefits: ['Hierarchical structure', 'Value table mapping', 'Implementation guides'],
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop'
    },
    {
      id: 'ricefw-tracker',
      title: 'RICEFW Tracker',
      description: 'Complete tracking system for Reports, Interfaces, Conversions, Enhancements, Forms, and Workflows',
      icon: 'ListChecks',
      color: 'bg-orange-500',
      benefits: ['All RICEFW types', 'Complexity scoring', 'Effort estimation'],
      thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop'
    },
    {
      id: 'bpd-generator',
      title: 'BPD² Generator',
      description: 'Automated Business Process Design Document generation with templating and version control',
      icon: 'FileText',
      color: 'bg-accent',
      benefits: ['Template library', 'Version control', 'PDF export'],
      thumbnail: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400&h=250&fit=crop'
    },
    {
      id: 'fsd-generator',
      title: 'FSD² Generator',
      description: 'Functional Specification Document creation with embedded diagrams and technical specifications',
      icon: 'Code',
      color: 'bg-red-500',
      benefits: ['Technical specs', 'Diagram integration', 'Code snippets'],
      thumbnail: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=250&fit=crop'
    }
  ];

  const handleExploreFeature = (featureId) => {
    switch (featureId) {
      case 'question-bank': navigate('/questionnaire-runner');
        break;
      case 'context-engine': navigate('/app-dashboard');
        break;
      case 'config-directory': navigate('/best-practice-picker');
        break;
      case 'ricefw-tracker': navigate('/fit-gap-board');
        break;
      case 'bpd-generator': case'fsd-generator': navigate('/document-generator');
        break;
      default:
        navigate('/app-dashboard');
    }
  };

  const handleStartEngagement = () => {
    navigate('/user-registration');
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-6">
            <Icon name="Zap" size={16} className="mr-2" />
            Product Features
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Enterprise-Grade SAP Tools
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive suite of tools designed specifically for SAP solution architects 
            to streamline implementation processes and ensure project success.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {features?.map((feature) => (
            <div key={feature?.id} className="bg-card border border-border rounded-xl overflow-hidden hover-lift hover:shadow-elevation-2 transition-all duration-300">
              {/* Feature Thumbnail */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={feature?.thumbnail} 
                  alt={feature?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className={`absolute top-4 left-4 w-12 h-12 ${feature?.color} rounded-lg flex items-center justify-center`}>
                  <Icon name={feature?.icon} size={24} className="text-white" />
                </div>
              </div>

              {/* Feature Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature?.title}
                </h3>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {feature?.description}
                </p>

                {/* Benefits List */}
                <ul className="space-y-2 mb-6">
                  {feature?.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-center text-sm text-muted-foreground">
                      <Icon name="Check" size={16} className="text-accent mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleExploreFeature(feature?.id)}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Explore Feature
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary-foreground mb-4">
              Ready to Transform Your SAP Implementation?
            </h3>
            
            <p className="text-xl text-primary-foreground/80 mb-8">
              Join thousands of SAP architects who trust BPMLflows to deliver 
              faster, more accurate implementations with complete digital compliance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={handleStartEngagement}
                className="bg-accent text-primary hover:bg-accent/90 px-8 py-4 text-lg font-semibold"
                iconName="Rocket"
                iconPosition="left"
              >
                Start Your First Engagement
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/pricing-plans')}
                className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 px-8 py-4 text-lg"
                iconName="DollarSign"
                iconPosition="left"
              >
                View Pricing
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center space-x-8 mt-8 pt-8 border-t border-primary-foreground/20">
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="Shield" size={20} />
                <span className="text-sm">SOC 2 Compliant</span>
              </div>
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="Lock" size={20} />
                <span className="text-sm">Enterprise Security</span>
              </div>
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="FileCheck" size={20} />
                <span className="text-sm">Audit Ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;